/**
 * UploadInvoiceModal. Lets a person add invoices that never arrived through the
 * watched-mailbox email intake (e.g. handed over in person, from an unwatched
 * vendor address). Each selected PDF is POSTed to
 * POST /v1/documents/invoices/upload (useUploadInvoice), one at a time, so the
 * invoice enters the same parse-and-store pipeline an emailed invoice does; the
 * api sets the stored mailbox field to "Uploaded by {user}" instead of a real
 * mailbox name. Mirrors SyncToSageModal's dialog chrome and error handling.
 */

import { useRef, useState } from 'react';
import { FileText, Loader2, Upload, X } from 'lucide-react';
import { useUploadInvoice } from '@/lib/useInvoices';

// Pull the server's human-readable message out of an api error body
// ({ error: { code, message } }), falling back to the thrown message. Mirrors
// SyncToSageModal's serverMessage.
function serverMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'body' in err) {
    const body = (err as { body?: unknown }).body;
    if (body && typeof body === 'object' && 'error' in body) {
      const e = (body as { error?: { message?: unknown } }).error;
      if (e && typeof e.message === 'string') return e.message;
    }
  }
  return err instanceof Error ? err.message : String(err);
}

type FileResult = { name: string; status: 'pending' | 'ok' | 'error'; message?: string };

type Props = {
  onClose: () => void;
  onUploaded?: () => void;
};

export function UploadInvoiceModal({ onClose, onUploaded }: Props) {
  const [files, setFiles] = useState<File[]>([]);
  const [results, setResults] = useState<FileResult[] | null>(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadInvoice();

  const addFiles = (list: FileList | null) => {
    if (!list) return;
    setFiles((prev) => [...prev, ...Array.from(list)]);
  };
  const removeFile = (idx: number) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const onRun = async () => {
    setUploading(true);
    const next: FileResult[] = files.map((f) => ({ name: f.name, status: 'pending' }));
    setResults(next);
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      if (!file) continue;
      try {
        await upload.mutateAsync(file);
        next[i] = { name: file.name, status: 'ok' };
      } catch (err) {
        next[i] = { name: file.name, status: 'error', message: serverMessage(err) };
      }
      setResults([...next]);
    }
    setUploading(false);
    if (next.some((r) => r.status === 'ok')) onUploaded?.();
  };

  const anyError = results?.some((r) => r.status === 'error') ?? false;
  const allDone = results != null && results.every((r) => r.status !== 'pending');

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="relative w-full max-w-md rounded-lg border border-amp-line bg-amp-white shadow-2 dark:border-amp-dark-line dark:bg-amp-dark-surface">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="p-5">
          <h2 className="text-sm font-semibold text-amp-ink dark:text-amp-dark-ink mb-1">Upload invoice</h2>
          <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-4">
            Adds an invoice that didn&apos;t arrive by email. It goes through the same parsing as an emailed invoice and shows up here once processed.
          </p>

          {results ? (
            <div className="space-y-3">
              <ul className="space-y-1.5">
                {results.map((r) => (
                  <li key={r.name} className="flex items-start gap-2 text-xs">
                    {r.status === 'pending' && <Loader2 className="h-3.5 w-3.5 mt-0.5 animate-spin text-amp-ink-muted dark:text-amp-dark-ink-muted" />}
                    {r.status === 'ok' && <FileText className="h-3.5 w-3.5 mt-0.5 text-green-700 dark:text-green-300" />}
                    {r.status === 'error' && <X className="h-3.5 w-3.5 mt-0.5 text-red-700 dark:text-red-300" />}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-amp-ink dark:text-amp-dark-ink">{r.name}</span>
                      {r.status === 'error' && (
                        <span className="block text-red-700 dark:text-red-300">{r.message}</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
              {allDone && (
                <div className="pt-1 flex justify-end">
                  <button
                    type="button"
                    onClick={onClose}
                    className="text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
                  >
                    {anyError ? 'Close' : 'Done'}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <input
                  ref={inputRef}
                  type="file"
                  accept="application/pdf"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    addFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
                <button
                  type="button"
                  onClick={() => inputRef.current?.click()}
                  className="inline-flex items-center gap-1 text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
                >
                  <Upload className="h-3 w-3" /> Choose PDF(s)
                </button>
                {files.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {files.map((f, i) => (
                      <li key={`${f.name}-${i}`} className="flex items-center justify-between gap-2 text-xs text-amp-ink dark:text-amp-dark-ink">
                        <span className="min-w-0 flex-1 truncate">{f.name}</span>
                        <button
                          type="button"
                          onClick={() => removeFile(i)}
                          aria-label={`Remove ${f.name}`}
                          className="text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onRun}
                  disabled={uploading || files.length === 0}
                  className="inline-flex items-center gap-1 text-xs border border-amp-ink bg-amp-ink text-amp-white rounded px-3 py-1.5 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-ink dark:bg-amp-dark-ink dark:text-amp-dark-surface"
                >
                  {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                  {uploading ? 'Uploading...' : `Upload ${files.length || ''}`.trim()}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
